import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material';
import {
  GridSortModel,
  GridPaginationModel,
  GridColDef
} from '@mui/x-data-grid';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';
import { useAtom } from 'jotai';
import {
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useParams } from 'react-router-dom';

import { useCompanyController } from 'api/controllers/CompanyController';
import { useIbanController } from 'api/controllers/IbanController';
import { useInvoiceController } from 'api/controllers/InvoiceController';

import {
  Company,
  CompanyIban,
  Invoice,
  InvoiceFilters,
  SortOrder,
  EntryType
} from 'openapi';

import { InvoicesDataGrid } from 'components/InvoicesDataGrid/InvoicesDataGrid';
import { Layout } from 'components/Layout/Layout';
import { OverlayLoading } from 'components/overlays/OverlayLoading';
import { Modal } from 'components/shared/Modal/Modal';

import { usePermissions } from 'context/PermissionsContext';
import { TokenExpirationModalContext } from 'context/TokenExpirationModalProvider';
import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';
import { usePagination } from 'hooks/usePagination';

import { getConfigByDocumentType } from 'utils/config/documentEntries';
import {
  ACCESS_TOKEN,
  BUTTON,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB
} from 'utils/constants/constants';
import { INITIAL_INVOICES_FILTERS } from 'utils/constants/invoices';
import { INITIAL_PAGINATION_PROPS } from 'utils/constants/paginations';
import { FilterType } from 'utils/enums/FilterType';
import { InvoiceEventType } from 'utils/enums/Invoice';
import { getInvoicesFiltersAtomByType } from 'utils/helpers/filtersHelpers';
import { handleSortModelChange } from 'utils/helpers/invoiceHelpers';
import { getEventData } from 'utils/helpers/serverSentEvents';
import { createSSEConnection } from 'utils/helpers/sseConnection';
import {
  getFileSizeWarningMessage,
  getInvoiceWarningMessage,
  showWarnMessage
} from 'utils/helpers/toastHelpers';
import { InvoicesFiltersForm } from 'utils/interfaces/InvoiceProps';
import { TranslateFunction } from 'utils/interfaces/Translations';
import {
  getMappedInvoiceFilters,
  getMappedInvoices
} from 'utils/mappers/invoice';
import { AppRoutesEnum } from 'utils/routes';

import {
  invoicesPaginationAtom,
  isRequestPendingAtom,
  userAtom,
  selectedCompanyIdAtom
} from 'state/state';

interface DocumentsUploadProps {
  filterType?: FilterType;
  isOnAllCompanies?: boolean;
  stateRoute: AppRoutesEnum;
  getColumns: (translate: TranslateFunction) => GridColDef[];
  entryType: EntryType;
}

export const DocumentsUpload = ({
  filterType,
  isOnAllCompanies,
  stateRoute,
  getColumns,
  entryType
}: DocumentsUploadProps) => {
  const { companyId } = useParams();
  const {
    addInvoice,
    getInvoiceFiltersByCompany,
    getInvoicesByCompanyAndFilters,
    getInvoiceRow,
    getFilteredInvoices,
    getInvoiceFiltersForAllCompanies
  } = useInvoiceController();
  const { getAllCompanies } = useCompanyController();
  const { getIbansByCompany } = useIbanController();
  const { translate } = useTranslations();
  const permissions = usePermissions();
  const { isRefreshedToken, handleRefresh } = useContext(
    TokenExpirationModalContext
  );

  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [blobArray, setBlobArray] = useState<FileList | File[] | Blob[]>();
  const [token, setToken] = useState<string>();

  const [pagination, setPagination] = useAtom(invoicesPaginationAtom);
  const [isRequestPending] = useAtom(isRequestPendingAtom);
  const [filters, setFilters] = useAtom(
    getInvoicesFiltersAtomByType(filterType || FilterType.Initial, stateRoute)
  );
  const [user] = useAtom(userAtom);
  const [selectedCompanyId, setSelectedCompanyId] = useAtom(
    selectedCompanyIdAtom
  );

  const { paginationModel, handlePaginationModelChange } = usePagination({
    page: pagination.page,
    pageSize: pagination.pageSize,
    rowCount: totalInvoices
  });

  const {
    isOpen: isSelectCompanyIdModalOpen,
    openModal: openSelectCompanyIdModal,
    closeModal: closeSelectCompanyIdModal
  } = useModal();

  const [invoices, setInvoices] = useState<Invoice[]>();
  const [invoicesFiltersData, setInvoicesFiltersData] =
    useState<InvoiceFilters>({});
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [companyIbans, setCompanyIbans] = useState<CompanyIban[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const filtersRef = useRef(filters);
  const isFilteredRef = useRef(isFiltered);
  const filterTypeRef = useRef(filterType);

  const setInvoicesArray = useCallback((action: SetStateAction<Invoice[]>) => {
    setInvoices(
      typeof action === 'function' ? (prev) => action(prev || []) : action
    );
  }, []);

  const fetchAllCompaniesInvoices = useCallback(async () => {
    const currentFilters = filtersRef.current;
    setIsLoading(true);
    const result = await getFilteredInvoices({
      ...currentFilters,
      pageNumber: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sortBy: currentFilters.sortBy,
      sortOrder: currentFilters.sortOrder?.toUpperCase() as SortOrder
    });

    const mappedInvoices = getMappedInvoices(result.invoices);

    setInvoices(mappedInvoices);
    setTotalInvoices(result.totalElements);
    setIsLoading(false);
  }, [paginationModel, getFilteredInvoices, filtersRef, filterType]);

  const fetchAllCompaniesInvoiceFilters = useCallback(async () => {
    const filtersData = await getInvoiceFiltersForAllCompanies();
    setInvoicesFiltersData(filtersData);
  }, [getInvoiceFiltersForAllCompanies]);

  const fetchCompanies = useCallback(async () => {
    const result = await getAllCompanies();
    setCompanies(result);
  }, [getAllCompanies]);

  const fetchCompanyInvoices = useCallback(async () => {
    setIsLoading(true);
    const currentFilters = filtersRef.current;
    const result = await getInvoicesByCompanyAndFilters(Number(companyId), {
      ...currentFilters,
      pageNumber: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sortBy: currentFilters.sortBy,
      sortOrder: currentFilters.sortOrder?.toUpperCase() as SortOrder
    });

    const mappedInvoices = getMappedInvoices(result.invoices);
    setInvoices(mappedInvoices);
    setTotalInvoices(result.totalElements || 0);
    setIsLoading(false);
  }, [
    companyId,
    paginationModel,
    getInvoicesByCompanyAndFilters,
    filtersRef,
    filterType
  ]);

  const fetchInvoiceFiltersByCompany = useCallback(async () => {
    const result = await getInvoiceFiltersByCompany(Number(companyId));
    setInvoicesFiltersData(result);
  }, [companyId]);

  const handleApplyFilters = useCallback(
    (data: InvoicesFiltersForm | null) => {
      const mappedFilters = getMappedInvoiceFilters(
        data || ({} as InvoicesFiltersForm)
      );
      setIsFiltered(!!data);
      // @ts-ignore
      setFilters({
        ...getConfigByDocumentType(entryType).initialFilters,
        ...mappedFilters
      });
      handlePaginationModelChange({
        ...paginationModel,
        page: INITIAL_PAGINATION_PROPS.page
      });
    },
    [setFilters, paginationModel]
  );

  const handleSortChange = useCallback(
    (sortModel: GridSortModel) => {
      handleSortModelChange(sortModel, setFilters);
    },
    [setFilters]
  );

  const handleUploadInvoice = async (
    fileData: FileList | File[],
    field: string
  ) => {
    let files: File[];
    setIsUploading(true);

    if (field === BUTTON) {
      files = Array.from(fileData as File[]);
      setBlobArray(files);
    } else {
      files = Array.from(fileData as File[]);
      setBlobArray(files);
    }

    const compressionOptions = {
      maxSizeMB: MAX_FILE_SIZE_MB - 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 1
    };

    const convertedFilesPromises = files.map(async (file) => {
      if (
        file.type === 'image/heic' ||
        file.name.toLowerCase().endsWith('.heic')
      ) {
        try {
          const blob = await heic2any({ blob: file, toType: 'image/jpeg' });
          const newFileName = `${file.name.replace(/\.[^/.]+$/, '')}.jpg`;
          const newFile = new File([blob as Blob], newFileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          const compressionOptionsWithFileName = {
            ...compressionOptions,
            fileName: newFileName
          };

          const compressedResult = await imageCompression(
            newFile,
            compressionOptionsWithFileName
          );

          let compressedFile;
          if (compressedResult instanceof File) {
            compressedFile = compressedResult;
          } else {
            compressedFile = new File([compressedResult], newFileName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
          }

          return compressedFile;
        } catch (error) {
          return file;
        }
      } else if (file.type.startsWith('image/') && file.type !== 'image/jpeg') {
        try {
          const newFileName = `${file.name.replace(/\.[^/.]+$/, '')}.jpg`;
          const compressionOptionsWithFileName = {
            ...compressionOptions,
            fileName: newFileName
          };

          const compressedResult = await imageCompression(
            file,
            compressionOptionsWithFileName
          );

          let compressedFile;
          if (compressedResult instanceof File) {
            compressedFile = compressedResult;
          } else {
            compressedFile = new File([compressedResult], newFileName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
          }

          return compressedFile;
        } catch (error) {
          return file;
        }
      } else {
        return file;
      }
    });

    const convertedFiles = await Promise.all(convertedFilesPromises);
    const validFiles: File[] = [];
    const invalidFileNames: string[] = [];

    convertedFiles.forEach((file) => {
      if (file.size <= MAX_FILE_SIZE) {
        validFiles.push(file);
      } else {
        invalidFileNames.push(file.name);
      }
    });

    if (isOnAllCompanies) {
      openSelectCompanyIdModal();
      return;
    }

    try {
      if (validFiles.length > 0) {
        await addInvoice(Number(companyId), {
          files: validFiles as Array<File>,
          entryType
        });
      }
    } finally {
      setIsUploading(false);
      if (invalidFileNames.length > 0) {
        const { label, message } = getFileSizeWarningMessage(invalidFileNames);
        showWarnMessage(
          translate(label, {
            maxFileSize: String(MAX_FILE_SIZE_MB),
            documents: message
          })
        );
      }
    }
  };

  const handlePagination = useCallback(
    (newPaginationModel: GridPaginationModel) => {
      setInvoices([]);
      handlePaginationModelChange(newPaginationModel);
    },
    [setInvoices, handlePaginationModelChange]
  );

  useEffect(() => {
    setToken(localStorage.getItem(ACCESS_TOKEN) || '');
  }, [localStorage.getItem(ACCESS_TOKEN), isRefreshedToken]);

  const handleInvoiceEvent = useCallback(
    async (data: string) => {
      const { eventId, eventType } = getEventData(data);
      const currentFilters = filtersRef.current;

      const shouldNotUpdate =
        eventType === InvoiceEventType.HEARTBEAT ||
        eventType === InvoiceEventType.IBAN_CONSENT ||
        isFilteredRef.current ||
        filterTypeRef.current !== FilterType.Initial ||
        paginationModel.page !== INITIAL_PAGINATION_PROPS.page ||
        currentFilters.sortBy !== INITIAL_INVOICES_FILTERS.sortBy ||
        currentFilters.sortOrder !== INITIAL_INVOICES_FILTERS.sortOrder;

      if (
        shouldNotUpdate &&
        eventType !== InvoiceEventType.INVOICE_DELETE &&
        eventType !== InvoiceEventType.INVOICE_DUPLICATE
      ) {
        return;
      }

      if (eventType === InvoiceEventType.INVOICE_DUPLICATE) {
        const { label, message } = getInvoiceWarningMessage(eventId);
        const duplicateMessage = translate(label, {
          invoices: message
        });

        showWarnMessage(duplicateMessage);
        return;
      }

      if (eventType === InvoiceEventType.INVOICE_CREATE) {
        const invoice = await getInvoiceRow(Number(eventId));
        const mappedInvoice = getMappedInvoices([invoice]);
        if (
          !isOnAllCompanies &&
          mappedInvoice[0].companyId !== Number(companyId)
        ) {
          return;
        }
        setInvoices((prevInvoices) => [
          ...mappedInvoice,
          ...(prevInvoices || [])
        ]);
        setTotalInvoices((prevTotalInvoices) => prevTotalInvoices + 1);
        return;
      }

      if (eventType === InvoiceEventType.INVOICE_UPDATE) {
        const invoice = await getInvoiceRow(Number(eventId));
        const mappedInvoice = getMappedInvoices([invoice]);
        if (
          !isOnAllCompanies &&
          mappedInvoice[0].companyId !== Number(companyId)
        ) {
          return;
        }
        setInvoices((prevInvoices) => {
          if (!prevInvoices) {
            return mappedInvoice;
          }
          if (prevInvoices.some((inv) => inv.id === mappedInvoice[0].id)) {
            return prevInvoices.map((inv) =>
              inv.id === mappedInvoice[0].id ? mappedInvoice[0] : inv
            );
          }
          return [...mappedInvoice, ...prevInvoices];
        });
        return;
      }

      if (isOnAllCompanies) {
        fetchAllCompaniesInvoices();
        return;
      }

      fetchCompanyInvoices();
    },
    [
      paginationModel,
      fetchCompanyInvoices,
      fetchAllCompaniesInvoices,
      invoices,
      isFiltered,
      filterTypeRef
    ]
  );

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    isFilteredRef.current = isFiltered;
  }, [isFiltered]);

  useEffect(() => {
    filterTypeRef.current = filterType;
  }, [filterType]);

  useEffect(() => {
    if (isOnAllCompanies) {
      fetchAllCompaniesInvoiceFilters();
      return;
    }
    fetchInvoiceFiltersByCompany();
  }, [
    isOnAllCompanies,
    fetchAllCompaniesInvoiceFilters,
    fetchInvoiceFiltersByCompany
  ]);

  useEffect(() => {
    setPagination({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize
    });
  }, [paginationModel]);

  useEffect(() => {
    if (isOnAllCompanies) {
      fetchAllCompaniesInvoices();
      return;
    }
    fetchCompanyInvoices();
  }, [
    filters,
    isOnAllCompanies,
    fetchCompanyInvoices,
    fetchAllCompaniesInvoices
  ]);

  useEffect(() => {
    if (!Object.keys(invoicesFiltersData).length || isRequestPending) {
      setIsLoading(true);
    }
  }, [invoicesFiltersData, isRequestPending]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const cleanup = createSSEConnection({
      token,
      userId: user?.id,
      onEvent: handleInvoiceEvent,
      onRefresh: handleRefresh,
      onTokenUpdated: (newToken: string) => setToken(newToken)
    });

    return cleanup;
  }, [token]);

  const getIbansByCompanyId = useCallback(async () => {
    const ibans = await getIbansByCompany(Number(companyId));
    setCompanyIbans(ibans);
  }, [getIbansByCompany, companyId, setCompanyIbans]);

  const handleCloseSelectCompanyIdModal = useCallback(() => {
    closeSelectCompanyIdModal();
    setIsUploading(false);
  }, [closeSelectCompanyIdModal]);

  useEffect(() => {
    if (isOnAllCompanies || !permissions.permissions.IBANS.read) {
      return;
    }
    getIbansByCompanyId();
  }, [getIbansByCompanyId]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    setPagination({
      page: INITIAL_PAGINATION_PROPS.page,
      pageSize: pagination.pageSize
    });

    handlePaginationModelChange({
      page: INITIAL_PAGINATION_PROPS.page,
      pageSize: pagination.pageSize
    });
  }, [entryType, filterType]);

  useEffect(() => {
    setInvoices(undefined);
  }, [filterType]);

  return (
    <Layout>
      {isUploading && <OverlayLoading />}
      {invoices ? (
        <>
          <InvoicesDataGrid
            invoicesList={invoices}
            filters={filters}
            setInvoicesList={setInvoicesArray}
            stateRoute={stateRoute}
            handleUploadInvoice={handleUploadInvoice}
            invoicesFiltersData={invoicesFiltersData}
            handleApplyFilters={handleApplyFilters}
            handlePaginationModelChange={handlePagination}
            paginationModel={paginationModel}
            rowCount={totalInvoices}
            handleSortModelChange={handleSortChange}
            fetchInvoices={
              isOnAllCompanies
                ? fetchAllCompaniesInvoices
                : fetchCompanyInvoices
            }
            isLoading={isLoading}
            companyIbans={companyIbans}
            companies={companies}
            getColumns={getColumns}
            entryType={entryType}
            filterType={filterType}
          />
          {companies?.length ? (
            <Modal
              headerTitle={translate('labels.uploadDocuments')}
              isOpen={isSelectCompanyIdModalOpen}
              hide={handleCloseSelectCompanyIdModal}
              maxWidth="sm"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <FormControl fullWidth>
                  <InputLabel>{`${translate('labels.company')}*`}</InputLabel>
                  <Select
                    label={translate('labels.company')}
                    value={selectedCompanyId || companies[0].id}
                    onChange={(e) =>
                      setSelectedCompanyId(Number(e.target.value))
                    }
                  >
                    {companies.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  sx={{ alignSelf: 'flex-end' }}
                  onClick={async () => {
                    closeSelectCompanyIdModal();
                    try {
                      await addInvoice(Number(selectedCompanyId), {
                        files: blobArray as Array<Blob>,
                        entryType
                      });
                    } finally {
                      setIsUploading(false);
                    }
                  }}
                >
                  {translate('buttons.upload')}
                </Button>
              </Box>
            </Modal>
          ) : null}
        </>
      ) : (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <CircularProgress />
        </Stack>
      )}
    </Layout>
  );
};
